import { z } from "zod";

const namePattern = /^[\p{L}\p{M}][\p{L}\p{M}\s.'-]{0,79}$/u;

const nameSchema = z
  .string()
  .trim()
  .min(1, "This field is required.")
  .max(80, "Use 80 characters or fewer.")
  .regex(namePattern, "Use letters, spaces, apostrophes or hyphens only.");

export const emailSchema = z
  .string()
  .trim()
  .min(3, "Enter an email address.")
  .max(254, "Use 254 characters or fewer.")
  .email("Enter a valid email address.")
  .transform((value) => value.toLowerCase());

export const consentRequestSchema = z
  .object({
    firstName: nameSchema,
    lastName: nameSchema,
    email: emailSchema,
    consents: z
      .object({
        currentRecruitment: z.literal(true, {
          errorMap: () => ({ message: "This consent is required." })
        }),
        futureRecruitment: z.literal(true, {
          errorMap: () => ({ message: "This consent is required." })
        }),
        shareWithClients: z.literal(true, {
          errorMap: () => ({ message: "This consent is required." })
        }),
        informationClause: z.literal(true, {
          errorMap: () => ({ message: "Please confirm that you have read the information clause." })
        }),
        privacyPolicy: z.literal(true, {
          errorMap: () => ({ message: "Please confirm that you have read the privacy policy." })
        }),
        withdrawalAcknowledged: z.literal(true, {
          errorMap: () => ({ message: "Please confirm that you understand how to withdraw consent." })
        })
      })
      .strict(),
    companyWebsite: z.string().max(200).optional().default("")
  })
  .strict();

export type ConsentRequest = z.infer<typeof consentRequestSchema>;

export type ConsentApiSuccess = {
  status: "received";
  submissionId: string;
  requestId: string;
};

export type ConsentApiError = {
  status: "error";
  message: string;
  requestId?: string;
  fields?: Record<string, string>;
};

export function zodErrorToFieldMap(error: z.ZodError): Record<string, string> {
  const fields: Record<string, string> = {};

  for (const issue of error.issues) {
    const path = issue.path.join(".");
    if (!path || fields[path]) {
      continue;
    }
    fields[path] = issue.message;
  }

  return fields;
}
